/**
 * Error Messages - Centralized error message constants
 *
 * RULES:
 * 1. ALWAYS use these constants for error messages
 * 2. NEVER hardcode error messages in components or services
 * 3. Keep messages clear and actionable
 *
 * FORMAT:
 * - Start with what went wrong
 * - Add context if necessary
 * - Suggest action when possible
 *
 * FUTURE: These constants can integrate with i18n for localization
 */

// =============================================================================
// AUTHENTICATION ERRORS
// =============================================================================

export const AUTH_ERRORS = {
  /** Firebase is not configured in environment */
  FIREBASE_NOT_CONFIGURED: 'Firebase is not configured. Authentication is disabled.',

  /** Firebase Auth service failed to initialize */
  FIREBASE_AUTH_NOT_INITIALIZED: 'Firebase Auth not initialized',

  /** Firestore service failed to initialize */
  FIRESTORE_NOT_INITIALIZED: 'Firestore not initialized',

  /** Google auth provider not available */
  GOOGLE_PROVIDER_NOT_INITIALIZED: 'Google provider not initialized',

  /** Facebook auth provider not available */
  FACEBOOK_PROVIDER_NOT_INITIALIZED: 'Facebook provider not initialized',

  /** User is not logged in */
  NOT_AUTHENTICATED: 'You must be logged in to perform this action.',

  /** Session has expired */
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',

  /** Invalid login credentials */
  INVALID_CREDENTIALS: 'Invalid email or password.',

  /** Email already registered */
  EMAIL_ALREADY_IN_USE: 'This email is already registered.',

  /** Password too weak */
  WEAK_PASSWORD: 'Password must be at least 6 characters.',
} as const;

// =============================================================================
// WEB3AUTH ERRORS
// =============================================================================

export const WEB3AUTH_ERRORS = {
  /** Web3Auth client ID not configured */
  CLIENT_ID_NOT_CONFIGURED: 'VITE_WEB3AUTH_CLIENT_ID is not configured',

  /** Web3Auth not initialized */
  NOT_INITIALIZED: 'Web3Auth not initialized. Call initialize() first.',

  /** Not ready for connection */
  NOT_READY: 'Web3Auth is not ready. Please try again.',

  /** Connection failed */
  CONNECTION_FAILED: 'Failed to connect to wallet',

  /** User not connected */
  NOT_CONNECTED: 'Not connected to Web3Auth',

  /** Failed to get wallet address */
  WALLET_ADDRESS_FAILED: 'Failed to get wallet address',
} as const;

// =============================================================================
// ELECTRON/DESKTOP ERRORS
// =============================================================================

export const ELECTRON_ERRORS = {
  /** Electron OAuth API not available */
  OAUTH_API_NOT_AVAILABLE: 'Electron OAuth API not available',

  /** No ID token from OAuth */
  NO_ID_TOKEN: 'No ID token received from OAuth',

  /** OAuth client ID not configured */
  OAUTH_CLIENT_ID_NOT_CONFIGURED:
    'Google sign-in is currently unavailable. Please try again later or use another sign-in method.',
} as const;

// =============================================================================
// NETWORK ERRORS
// =============================================================================

export const NETWORK_ERRORS = {
  /** Failed to connect to server */
  CONNECTION_FAILED: 'Failed to connect to the server. Please check your internet connection.',

  /** Request timed out */
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',

  /** Server error */
  SERVER_ERROR: 'Server error. Please try again later.',

  /** Resource not found */
  NOT_FOUND: 'The requested resource was not found.',

  /** Too many requests */
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',

  /** Circuit breaker open */
  CIRCUIT_BREAKER_OPEN: 'Circuit breaker is OPEN - service is temporarily unavailable',
} as const;

// =============================================================================
// API CONFIGURATION ERRORS
// =============================================================================

export const API_CONFIG_ERRORS = {
  /** Serverless API URL not configured */
  SERVERLESS_NOT_CONFIGURED: 'Serverless API URL is not configured. No fallbacks.',

  /** Local API URL not configured */
  LOCAL_NOT_CONFIGURED: 'Local API URL is not configured. No fallbacks.',
} as const;

// =============================================================================
// FILE ERRORS
// =============================================================================

export const FILE_ERRORS = {
  /** File is too large */
  FILE_TOO_LARGE: 'File is too large. Maximum size is {maxSize}.',

  /** Invalid file type */
  INVALID_FILE_TYPE: 'Invalid file type. Supported types: {types}.',

  /** Upload failed */
  UPLOAD_FAILED: 'Failed to upload file. Please try again.',

  /** Download failed */
  DOWNLOAD_FAILED: 'Failed to download file. Please try again.',

  /** Could not detect media type */
  MEDIA_TYPE_DETECTION_FAILED: 'Could not detect media type',
} as const;

// =============================================================================
// VALIDATION ERRORS
// =============================================================================

export const VALIDATION_ERRORS = {
  /** Required field is empty */
  REQUIRED_FIELD: 'This field is required.',

  /** Invalid email format */
  INVALID_EMAIL: 'Please enter a valid email address.',

  /** Invalid URL format */
  INVALID_URL: 'Please enter a valid URL.',

  /** Below minimum length */
  MIN_LENGTH: 'Must be at least {min} characters.',

  /** Above maximum length */
  MAX_LENGTH: 'Must be at most {max} characters.',

  /** Invalid response from server */
  INVALID_RESPONSE: 'Invalid response: missing {field}',
} as const;

// =============================================================================
// GENERATION ERRORS
// =============================================================================

export const GENERATION_ERRORS = {
  /** Generation failed */
  GENERATION_FAILED: 'Failed to generate content. Please try again.',

  /** Processing failed */
  PROCESSING_FAILED: 'Processing failed. Please try again.',

  /** Generation timed out */
  GENERATION_TIMED_OUT: 'Generation timed out',

  /** Video generation timed out */
  VIDEO_GENERATION_TIMED_OUT: 'Video generation timeout - took too long to complete',

  /** No images generated */
  NO_IMAGES_GENERATED: 'No images generated',

  /** Insufficient credits */
  INSUFFICIENT_CREDITS: 'Insufficient credits. Please add more credits to continue.',

  /** Quota exceeded */
  QUOTA_EXCEEDED: 'Quota exceeded. Please try again tomorrow.',

  /** Audio URL not available */
  AUDIO_URL_NOT_AVAILABLE: 'Audio URL not available',

  /** Failed to decode audio */
  AUDIO_DECODE_FAILED: 'Failed to decode audio',

  /** No job ID returned */
  NO_JOB_ID: 'No job ID returned from server',

  /** Nothing to regenerate */
  NO_GENERATION_TO_REGENERATE: 'No current generation to regenerate',
} as const;

// =============================================================================
// CONVERSION ERRORS
// =============================================================================

export const CONVERSION_ERRORS = {
  /** Conversion failed */
  CONVERSION_FAILED: 'Conversion failed',

  /** Conversion timed out */
  CONVERSION_TIMED_OUT: 'Conversion timed out',

  /** Failed to get conversion result */
  RESULT_FAILED: 'Failed to get conversion result',

  /** Failed to check conversion status */
  STATUS_CHECK_FAILED: 'Failed to check conversion status',
} as const;

// =============================================================================
// CONTEXT ERRORS (React Hooks)
// =============================================================================

export const CONTEXT_ERRORS = {
  /** useTheme used outside ThemeProvider */
  USE_THEME_OUTSIDE_PROVIDER: 'useTheme must be used within ThemeProvider',

  /** useI18n used outside I18nProvider */
  USE_I18N_OUTSIDE_PROVIDER: 'useI18n must be used within I18nProvider',

  /** useSettings used outside SettingsProvider */
  USE_SETTINGS_OUTSIDE_PROVIDER: 'useSettings must be used within SettingsProvider',

  /** useAuth used outside AuthProvider */
  USE_AUTH_OUTSIDE_PROVIDER: 'useAuth must be used within an AuthProvider',
} as const;

// =============================================================================
// CANVAS ERRORS
// =============================================================================

export const CANVAS_ERRORS = {
  /** Canvas not available */
  CANVAS_NOT_AVAILABLE: 'Canvas not available',

  /** Brand kit not found */
  BRAND_KIT_NOT_FOUND: 'Brand kit not found',

  /** At least one image required */
  IMAGE_REQUIRED: 'At least one image is required for analysis',

  /** Failed to load templates */
  TEMPLATES_LOAD_FAILED: 'Failed to load starter templates index',
} as const;

// =============================================================================
// GENERIC ERRORS
// =============================================================================

export const GENERIC_ERRORS = {
  /** Unknown error */
  UNKNOWN: 'An unexpected error occurred. Please try again.',

  /** Operation failed */
  OPERATION_FAILED: 'Operation failed. Please try again.',

  /** Network error */
  NETWORK_ERROR: 'Network error',
} as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Substitute placeholders in error message with values.
 *
 * @example
 * formatError(FILE_ERRORS.FILE_TOO_LARGE, { maxSize: '500 MB' })
 * // Returns: "File is too large. Maximum size is 500 MB."
 *
 * @param message - Error message with placeholders like {key}
 * @param params - Object with placeholder values
 * @returns Formatted error message
 */
export function formatError(message: string, params: Record<string, string | number>): string {
  let result = message;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`{${key}}`, String(value));
  }
  return result;
}

/**
 * Get human-readable error message for HTTP status code.
 *
 * @param status - HTTP status code
 * @returns Human-readable error message
 */
export function getHttpErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return AUTH_ERRORS.NOT_AUTHENTICATED;
    // Payment Required. Unmapped, this fell through to "HTTP error 402" — the
    // last-resort message for a refusal the user can act on in one click.
    case 402:
      return GENERATION_ERRORS.INSUFFICIENT_CREDITS;
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return NETWORK_ERRORS.NOT_FOUND;
    case 429:
      return NETWORK_ERRORS.RATE_LIMITED;
    case 500:
    case 502:
    case 503:
      return NETWORK_ERRORS.SERVER_ERROR;
    default:
      return `HTTP error ${status}`;
  }
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type AuthError = (typeof AUTH_ERRORS)[keyof typeof AUTH_ERRORS];
export type Web3AuthError = (typeof WEB3AUTH_ERRORS)[keyof typeof WEB3AUTH_ERRORS];
export type ElectronError = (typeof ELECTRON_ERRORS)[keyof typeof ELECTRON_ERRORS];
export type NetworkError = (typeof NETWORK_ERRORS)[keyof typeof NETWORK_ERRORS];
export type FileError = (typeof FILE_ERRORS)[keyof typeof FILE_ERRORS];
export type ValidationError = (typeof VALIDATION_ERRORS)[keyof typeof VALIDATION_ERRORS];
export type GenerationError = (typeof GENERATION_ERRORS)[keyof typeof GENERATION_ERRORS];
export type ConversionError = (typeof CONVERSION_ERRORS)[keyof typeof CONVERSION_ERRORS];
export type ContextError = (typeof CONTEXT_ERRORS)[keyof typeof CONTEXT_ERRORS];
export type CanvasError = (typeof CANVAS_ERRORS)[keyof typeof CANVAS_ERRORS];
export type GenericError = (typeof GENERIC_ERRORS)[keyof typeof GENERIC_ERRORS];
